/**
 * Sections « À télécharger » (factures qu'un message ne livre que par un lien :
 * la personne télécharge la facture et dépose le fichier ici) et « Déposer un
 * fichier » (reçu papier photographié, facture téléchargée de son côté).
 * Le fichier part tel quel, en application/octet-stream.
 */

import { h } from '../../dom.js';
import { deposerFichier, ecarterPiece, ErreurPiece, TAILLE_DEPOT_MAX, type Piece, type PieceListee } from './api.js';
import { creerRetour, creerSection, messageDe, resumeProvenance, verrouille, type Contexte, type Retour, type Section } from './communs.js';
import { sectionDeCartes, type ListeCartes, type SectionCartes } from './liste-cartes.js';
import { ETATS_PIECE, messageEnvoiRefuse, refusAvantEnvoi } from './pieces-textes.js';

const TYPES_ADMIS = '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic';

function selecteurFichier(): { readonly el: HTMLElement; readonly entree: HTMLInputElement } {
  const entree = h('input', { type: 'file', accept: TYPES_ADMIS });
  return { el: h('label', {}, 'Fichier (PDF, JPEG, PNG ou HEIC, 20 Mo au plus) ', entree), entree };
}

/** Envoie le fichier choisi ; les refus du serveur sont traduits en français. */
async function envoyer(entree: HTMLInputElement, retour: Retour, aTelecharger?: number): Promise<{ piece: Piece; doublon: boolean } | null> {
  const fichier = entree.files?.[0];
  const refus = refusAvantEnvoi(fichier, TAILLE_DEPOT_MAX);
  if (refus !== null || fichier === undefined) {
    retour.ko(refus ?? 'Choisissez d’abord un fichier.');
    return null;
  }
  try {
    return await deposerFichier(fichier, aTelecharger);
  } catch (e) {
    retour.ko(e instanceof ErreurPiece ? messageEnvoiRefuse(e.statut, e.message) : 'Le fichier n’a pas pu être envoyé. Réessayez.');
    return null;
  }
}

function carteTelecharger(p: PieceListee, liste: ListeCartes, ctx: Contexte): HTMLElement {
  const retour = creerRetour();
  const choix = selecteurFichier();
  const deposer = h('button', { type: 'button', classe: 'btn-action principal' }, 'Déposer le fichier');
  const ecarter = h('button', { type: 'button', classe: 'btn-action' }, 'Écarter…');
  const motif = h('input', { type: 'text', maxlength: 300, classe: 'champ-motif', placeholder: 'Pourquoi cette facture n’est pas à classer' });
  const confirmer = h('button', { type: 'button', classe: 'btn-action' }, 'Confirmer l’écart');
  const annuler = h('button', { type: 'button', classe: 'btn-action' }, 'Annuler');
  const formEcart = h('div', { classe: 'ecart-piece', hidden: true }, h('label', {}, 'Motif ', motif), confirmer, annuler);
  const tous = [choix.entree, deposer, ecarter, motif, confirmer, annuler];

  deposer.addEventListener('click', () => {
    void (async () => {
      retour.effacer();
      const r = await verrouille(tous, () => envoyer(choix.entree, retour, p.id));
      if (r === null) return;
      liste.retirer(p.id);
      ctx.retour.ok(`Fichier reçu : la pièce n° ${String(r.piece.id)} est maintenant « ${ETATS_PIECE[r.piece.etat].toLowerCase()} ».`);
      ctx.recharger('valider', 'arbitrer', 'historique');
    })();
  });

  ecarter.addEventListener('click', () => {
    formEcart.hidden = false;
    motif.focus();
  });
  annuler.addEventListener('click', () => {
    formEcart.hidden = true;
  });
  confirmer.addEventListener('click', () => {
    const m = motif.value.trim();
    if (m === '') {
      retour.ko('Le motif est obligatoire : il reste dans l’historique.');
      return;
    }
    void (async () => {
      try {
        retour.effacer();
        await verrouille(tous, () => ecarterPiece(p.id, m));
        liste.retirer(p.id);
        ctx.retour.ok('Demande écartée. Elle reste dans l’historique.');
        ctx.recharger('historique');
      } catch (e) {
        retour.ko(messageDe(e, 'La demande n’a pas pu être écartée.'));
      }
    })();
  });

  return h(
    'article',
    { classe: 'carte-piece carte-simple', 'aria-label': `Demande ${String(p.id)}` },
    h(
      'div',
      { classe: 'carte-corps' },
      resumeProvenance(p.provenance),
      h('p', { classe: 'note-lecture' }, 'La facture est derrière un lien du message : ouvrez le message, téléchargez-la, puis déposez-la ici.'),
      h('div', { classe: 'editeur-champs' }, choix.el),
      h('div', { classe: 'barre-actions' }, deposer, ecarter),
      formEcart,
      retour.el,
    ),
  );
}

export function sectionTelecharger(ctx: Contexte): SectionCartes {
  return sectionDeCartes(
    3,
    'À télécharger',
    'Factures que le message ne livre que par un lien',
    'a_telecharger',
    (p, liste) => carteTelecharger(p, liste, ctx),
    'Aucune facture à télécharger.',
  );
}

export function sectionDeposer(ctx: Contexte): Section {
  const section = creerSection(4, 'Déposer un fichier', 'Un reçu photographié, une facture téléchargée : elle suit le même parcours');
  const retour = creerRetour();
  const choix = selecteurFichier();
  const bouton = h('button', { type: 'button', classe: 'btn-action principal' }, 'Déposer le fichier');

  bouton.addEventListener('click', () => {
    void (async () => {
      retour.effacer();
      const r = await verrouille([choix.entree, bouton], () => envoyer(choix.entree, retour));
      if (r === null) return;
      choix.entree.value = '';
      const etat = ETATS_PIECE[r.piece.etat].toLowerCase();
      if (r.doublon) {
        retour.ok(`Ce fichier est déjà connu : c’est la pièce n° ${String(r.piece.id)} (${etat}). Votre dépôt est noté dans son historique.`);
      } else {
        retour.ok(`Fichier reçu : pièce n° ${String(r.piece.id)}, ${etat}.`);
      }
      ctx.recharger('valider', 'arbitrer', 'historique');
    })();
  });

  section.corps.append(
    h('p', { classe: 'note-lecture' }, 'Le fichier est lu comme une facture du SCC et proposé à la validation, avec son auteur pour provenance.'),
    h('div', { classe: 'editeur-champs' }, choix.el, bouton),
    retour.el,
  );
  return section;
}
